import { motion } from 'framer-motion';
import { RADIUS, innerRadius, OPAQUE_PANEL_BG, TEXT_PRIMARY, ACCENT_GOLD, GOLD_TEXT } from '../../lib/medievalTheme';
import { getClass } from '../../utils/arena/classes';
import { playUI, UI } from '../../utils/arena/uiSounds';

const ACCENT = getClass('oracle').accent;
// The plates are the art box alone (scripts/tarot-plates.cjs, 480×780); the
// card is Valkenhall's: the panel's dark ground, the parchment text, the
// tapered gold rule the app draws under a heading.
const PLATE_RATIO = 780 / 480;

// The prints' paper grain (scripts/tarot-grain.cjs), laid faintly over the
// dark ground at the plate's pixel pitch so it reads as the same sheet.
const GRAIN = '/mastery/tarot/paper.webp';
const GRAIN_PX = 320;

// A tarot plate floating over the board, laid out like one of our own
// cards: the art, the name band, then the reading's text on the ground.
// `width` in cqw; everything follows it.
export default function TarotCard({ art, name, caption, tag, width = 13, selected = false, dimmed = false, gilded = false, onClick, testId }) {
  const interactive = typeof onClick === 'function';
  const pad = width * 0.045;
  const plateW = width - 2 * pad;
  const plateH = plateW * PLATE_RATIO;
  const grain = `${(plateW / 480) * GRAIN_PX}cqw`;
  const stroke = selected ? ACCENT : gilded ? ACCENT_GOLD : `${GOLD_TEXT} 0.35)`;
  return (
    <motion.div
      className="relative select-none self-stretch"
      style={{
        width: `${width}cqw`,
        padding: `${pad}cqw`,
        borderRadius: RADIUS.md,
        backgroundColor: OPAQUE_PANEL_BG,
        backgroundImage: `url(${GRAIN})`,
        backgroundSize: grain,
        boxShadow: [
          `0 0 0 1px ${stroke}`,
          selected ? `0 0 0 0.1875cqw ${ACCENT}` : null,
          selected ? `0 0 2.5cqw ${ACCENT}80` : null,
          '0 1.25cqw 2.5cqw rgba(0,0,0,0.6)',
          '0 0.25cqw 0.5cqw rgba(0,0,0,0.5)',
        ].filter(Boolean).join(', '),
        cursor: interactive ? 'pointer' : undefined,
        filter: dimmed ? 'brightness(0.55) saturate(0.7)' : 'none',
        transitionProperty: 'box-shadow, filter',
        transitionDuration: '220ms',
        transitionTimingFunction: 'cubic-bezier(0.2, 0, 0, 1)',
      }}
      whileHover={interactive ? { y: '-0.625cqw' } : undefined}
      whileTap={interactive ? { scale: 0.96 } : undefined}
      animate={{ y: selected ? '-0.875cqw' : 0, scale: selected ? 1.04 : 1 }}
      transition={{ type: 'spring', duration: 0.35, bounce: 0 }}
      onClick={onClick}
      onMouseEnter={interactive ? () => playUI(UI.HOVER, { volume: 0.3 }) : undefined}
      data-testid={testId}
      data-selected={selected || undefined}
    >
      <img
        src={art} alt="" draggable={false} className="block"
        style={{ width: `${plateW}cqw`, height: `${plateH}cqw`, borderRadius: innerRadius(RADIUS.md, `${pad}cqw`), filter: 'brightness(0.9) saturate(0.92)' }}
      />
      {name ? (
        <div className="text-center" style={{ paddingTop: `${width * 0.045}cqw` }}>
          <div
            className="arena-heading"
            style={{
              color: TEXT_PRIMARY,
              textShadow: '0 1px 0 rgba(0,0,0,0.7), 0 0 0.75cqw rgba(232, 213, 160, 0.18)',
              fontSize: `${Math.max(width * 0.085, 1)}cqw`,
              letterSpacing: '0.06em',
              lineHeight: 1.1,
            }}
          >
            {name}
          </div>
          {/* The heading rule the rest of the app draws under a title: a
              tapered gold hairline, no box. */}
          <div
            aria-hidden
            style={{
              height: '1px',
              width: '70%',
              margin: `${width * 0.03}cqw auto 0`,
              background: `linear-gradient(90deg, transparent, ${GOLD_TEXT} 0.7), transparent)`,
            }}
          />
        </div>
      ) : null}
      {caption ? (
        <div
          className="text-center arena-heading"
          style={{
            padding: `${width * 0.05}cqw ${width * 0.04}cqw ${width * 0.02}cqw`,
            color: TEXT_PRIMARY,
            textShadow: '0 1px 0 rgba(0,0,0,0.7)',
            fontSize: `${Math.max(width * 0.06, 0.8)}cqw`,
            lineHeight: 1.3,
            letterSpacing: '0.02em',
            textWrap: 'pretty',
          }}
        >
          {caption}
          {tag ? (
            <div style={{ marginTop: `${width * 0.02}cqw`, color: ACCENT_GOLD, fontSize: `${Math.max(width * 0.05, 0.7)}cqw`, letterSpacing: '0.04em' }}>
              {tag}
            </div>
          ) : null}
        </div>
      ) : null}
    </motion.div>
  );
}
